import {
  Component,
  Input,
  OnInit,
  inject,
  Output,
  EventEmitter,
  ViewChild,
  OnChanges,
  SimpleChanges,
  ElementRef,
  Renderer2,
  ChangeDetectorRef
} from '@angular/core';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { Alert, AlertService, AlertType, PaginatorFetcher, PaginatorFilters, TableComponent, TableConfig } from '@michelin/theme';
import { WorkOrderService } from '../../services/workorder.service';
import { debounceTime, distinctUntilChanged, map } from 'rxjs';
import { Store } from '@ngxs/store';
import { DealerState } from '../../../../shared/state/DealerState';
import { Dealer } from '../../../../shared/dealer/models/dealer';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { WorkOrderLineView } from './model/work-order-line-view.model';
import { PaginatedWOLResponse } from './model/paginated-wol.model';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { isDeletableCasingStatus, isWorkOrderLineEditable } from '../../shared/util/work-order-helper-util';
import { UpdateCurrentPaginatedWorkOrderLinesAction, WorkOrderState } from '../../../../shared/state/WorkOrderState';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { MatDialog } from '@angular/material/dialog';
import { SharedDeleteModalComponent } from '../../../../shared/components/shared-delete-modal/shared-dialog.component';
import { UserState } from '../../../../shared/state/UserState';
import { ColumnPreferencesHelperService } from '../../../../shared/services/column-preferences-helper.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-work-order-line-list',
  templateUrl: './work-order-line-list.component.html',
  styleUrls: ['./work-order-line-list.component.scss'],
  standalone: false
})
export class WorkOrderLineListComponent implements OnInit, OnChanges {
  dealer: Dealer;
  currentUserName: string;

  @Input() workOrderId!: string;
  @Output() fetchComplete = new EventEmitter<void>();
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<Element>;
  @Input() refreshWolList: number | null = null;
  public router = inject(Router);
  public workOrderService = inject(WorkOrderService);
  public featureflagsService = inject(FeatureflagsService);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public alertService = inject(AlertService);
  public dialog = inject(MatDialog);
  public columnPrefsHelper = inject(ColumnPreferencesHelperService);
  public cdr = inject(ChangeDetectorRef);
  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);

  public isDeletableCasingStatus = isDeletableCasingStatus;
  isDeleteWorkOrderEnabled = false;

  searchControl: FormControl = new FormControl<string>('');

  selectedWorkOrderLines = new Array<WorkOrderLineView>();
  allWorkOrderLines: WorkOrderLineView[] = [];
  private readonly paginationStoragePrefix = 'workorder-line-list-';

  tableConfig: TableConfig<WorkOrderLineView> = {
    paginator: {
      style: 'pageNumber',
      pageSize: 10,
      pageSizeOptions: [5, 10, 25, 100],
      forceBottomAction: true,
      position: 'bottom'
    },
    storage: {
      type: localStorage,
      options: {
        prefix: this.paginationStoragePrefix,
        keys: [],
        // Persist paginator metadata across navigation within allowed detail flow
        paginator: true,
        columns: true,
        tags: false
      }
    },
    onSelectionChange: (elms: WorkOrderLineView[]) => {
      this.selectedWorkOrderLines = elms;
    },
    selection: {
      columnKeyForCheckboxLabel: 'name',
      allLabel: 'All'
    },
    columns: {}
  };

  // column selector state
  private canonicalColumns: Record<string, any> = {};
  columnKeys: string[] = [];
  columnOrder: string[] = [];
  columnVisibility: Record<string, boolean> = {};
  columnNames: Record<string, string> = {};

  // Data Customization Control positioning (AC2-AC4): keep the column selector
  // and paginator in the same row when the controls are rendered above the table.
  private readonly SMALL_PAGE_SIZE_THRESHOLD = 10;

  private updateColumnSelectorPosition(pageSize: number): void {
    if (!this.tableConfig.paginator) {
      return;
    }
    const useBottomControls = pageSize <= this.SMALL_PAGE_SIZE_THRESHOLD;
    this.tableConfig.paginator.forceBottomAction = useBottomControls;
    this.tableConfig.paginator.position = useBottomControls ? 'bottom' : 'top';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['refreshWolList'] && !changes['refreshWolList'].firstChange) {
      this.tableRef.resetFilters();
    }
  }

  ngOnInit(): void {
    this.dealer = this.store.selectSnapshot(DealerState.currentDealer);
    this.currentUserName = this.store.selectSnapshot(UserState.getUserName);
    this.isDeleteWorkOrderEnabled = this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.DELETE_WORK_ORDER_ENABLED);

    this.searchControl.valueChanges.pipe(debounceTime(400), distinctUntilChanged()).subscribe(() => {
      if (this.tableRef) {
        this.tableRef.patchExtraFilters({ searchString: this.searchControl.value });
      }
    });

    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      // canonical columns (source of truth for selector and persistence)
      this.canonicalColumns = {
        lineNumber: {
          cell: (item: WorkOrderLineView) => item.lineNumber,
          header: this.translationService.translate('lineNumberText')
        },
        barCodeNumber: {
          cell: (item: WorkOrderLineView) => item.casingId,
          header: this.translationService.translate('barCodeNumberText')
        },
        casingStatus: {
          cell: (item: WorkOrderLineView) => item.casingStatus,
          header: this.translationService.translate('statusText')
        },
        productionCustomer: {
          cell: (item: WorkOrderLineView) => item.prodCustomer,
          header: this.translationService.translate('productionCustomerText')
        },
        location: {
          cell: (item: WorkOrderLineView) => item.shippingLocation,
          header: this.translationService.translate('locationText')
        },
        processCode: {
          cell: (item: WorkOrderLineView) => item.processCode,
          header: this.translationService.translate('processCodeText')
        },
        workType: {
          cell: (item: WorkOrderLineView) => item.workType,
          header: this.translationService.translate('workTypeText')
        },
        postMarker: {
          cell: (item: WorkOrderLineView) => item.post,
          header: this.translationService.translate('postText')
        },
        make: {
          cell: (item: WorkOrderLineView) => item.make,
          header: this.translationService.translate('makeText')
        },
        series: {
          cell: (item: WorkOrderLineView) => item.series,
          header: this.translationService.translate('seriesText')
        },
        size: {
          cell: (item: WorkOrderLineView) => item.size,
          header: this.translationService.translate('sizeText')
        },
        tread: {
          cell: (item: WorkOrderLineView) => item.treadDesc,
          header: this.translationService.translate('treadText')
        },
        mDot: {
          cell: (item: WorkOrderLineView) => item.mdot,
          header: this.translationService.translate('mfgDotText')
        },
        brandNumber: {
          cell: (item: WorkOrderLineView) => item.brand,
          header: this.translationService.translate('brandNumberText')
        },
        rejectCode: {
          cell: (item: WorkOrderLineView) => item.rejectCode,
          header: this.translationService.translate('rejectCodeText')
        },
        rejectReason: {
          cell: (item: WorkOrderLineView) => item.rejectReason,
          header: this.translationService.translate('rejectReasonText')
        },
        invoiceNumber: {
          cell: (item: WorkOrderLineView) => item.invoiceNumber,
          header: this.translationService.translate('invoiceNumberText')
        },
        repairs: {
          cell: (item: WorkOrderLineView) => item.repairs,
          header: this.translationService.translate('repairsText')
        }
      };

      // initialize selector labels and persisted prefs using shared helper
      const init = this.columnPrefsHelper.initialize('workorder-line-list', this.canonicalColumns, {
        defaultHidden: ['mDot', 'brandNumber', 'rejectCode', 'rejectReason', 'invoiceNumber', 'repairs']
      });
      this.tableConfig.columns = init.tableColumns;
      this.columnNames = init.columnNames;
      this.columnKeys = init.columnKeys;
      this.columnOrder = init.columnOrder;
      this.columnVisibility = init.columnVisibility;

      this.tableConfig.onColumnsChange = (columns: string[]) => {
        Object.keys(this.tableConfig.columns).forEach((key) => {
          this.tableConfig.columns[key].display = columns.includes(key);
        });
      };

      this.tableConfig.onRowClick = (workOrderLine: WorkOrderLineView) => {
        this.router.navigate(['/home/work-orders/work-order-line-details', this.workOrderId, workOrderLine.id], {
          queryParams: { isEditable: isWorkOrderLineEditable(workOrderLine.casingStatus, workOrderLine.lockPost) }
        });
      };

      // ensure change detection after columns setup
      this.cdr.markForCheck();
    });
  }

  // column initialization and persistence handled by ColumnPreferencesHelperService

  // Toggle visibility for a column and persist preferences
  toggleColumn(key: string): void {
    const result = this.columnPrefsHelper.toggleAndSave(
      'workorder-line-list',
      key,
      this.canonicalColumns,
      this.columnOrder,
      this.columnVisibility
    );
    this.tableConfig = {
      ...this.tableConfig,
      columns: result.tableColumns
    };
    this.columnVisibility = result.columnVisibility;
    this.cdr.markForCheck();
  }

  onColumnDrop(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    moveItemInArray(this.columnOrder, event.previousIndex, event.currentIndex);

    const columns: Record<string, any> = {};
    this.columnOrder.forEach((key) => {
      if (this.columnVisibility[key]) {
        columns[key] = this.canonicalColumns[key];
      }
    });

    this.tableConfig = {
      ...this.tableConfig,
      columns
    };
    this.columnPrefsHelper.save('workorder-line-list', this.columnOrder, this.columnVisibility, this.canonicalColumns);
    this.cdr.markForCheck();
  }

  // savePrefs is handled by ColumnPreferencesHelperService when toggling columns

  private addPaginatorTooltip(): void {
    try {
      const paginatorRangeLabel = this.elementRef.nativeElement.querySelector('.mat-mdc-paginator-range-label');
      if (paginatorRangeLabel) {
        this.renderer.setAttribute(paginatorRangeLabel, 'title', this.translationService.translate('numLinesHoverText'));
        this.renderer.setStyle(paginatorRangeLabel, 'cursor', 'help');
      }
    } catch (error) {
      console.warn('Could not add tooltip to paginator:', error);
    }
  }

  private addProcessCodeTooltip(): void {
    try {
      const processCodeCells = this.elementRef.nativeElement.querySelectorAll('td.mat-column-processCode');
      processCodeCells.forEach((cell: HTMLElement) => {
        switch (cell.innerText.trim()) {
          case 'DS':
            this.renderer.setAttribute(cell, 'title', this.translationService.translate('dsProcessCodeText'));
            break;
          case 'BS':
            this.renderer.setAttribute(cell, 'title', this.translationService.translate('bsProcessCodeText'));
            break;
          case 'CC':
            this.renderer.setAttribute(cell, 'title', this.translationService.translate('ccProcessCodeText'));
            break;
          case 'CP':
            this.renderer.setAttribute(cell, 'title', this.translationService.translate('cpProcessCodeText'));
            break;
          case 'RR':
            this.renderer.setAttribute(cell, 'title', this.translationService.translate('rrProcessCodeText'));
            break;
          case 'RO':
            this.renderer.setAttribute(cell, 'title', this.translationService.translate('roProcessCodeText'));
            break;
          case 'IO':
            this.renderer.setAttribute(cell, 'title', this.translationService.translate('ioProcessCodeText'));
            break;
          case 'SC':
            this.renderer.setAttribute(cell, 'title', this.translationService.translate('scProcessCodeText'));
            break;
          case 'AD':
            this.renderer.setAttribute(cell, 'title', this.translationService.translate('adProcessCodeText'));
            break;
          case 'SO':
            this.renderer.setAttribute(cell, 'title', this.translationService.translate('soProcessCodeText'));
            break;
          case 'NP':
            this.renderer.setAttribute(cell, 'title', this.translationService.translate('npProcessCodeText'));
            break;
          default:
        }
      });
    } catch (error) {
      console.warn('Could not add tooltip to process code cells:', error);
    }
  }

  fetcher: PaginatorFetcher<WorkOrderLineView> = (filters: PaginatorFilters) => {
    const rawSearchString = (this.searchControl.value ?? '').trim();
    console.log('Fetching work order lines with filters:', filters, 'and search string:', rawSearchString);
    // Reposition the Show/Hide Data (column selector) button per AC2-AC4.
    this.updateColumnSelectorPosition(filters.limit);
    return this.workOrderService
      .searchWorkOrderLines(this.dealer.id, this.workOrderId, filters.page, filters.limit, null, rawSearchString)
      .pipe(
        map((response: PaginatedWOLResponse) => {
          if (response && response.error && response.error.length > 0) {
            this.alertService.showAlert({
              type: AlertType.strong,
              content: this.translationService.translate('tryAgainText')
            } as Alert);
            return;
          } else {
            const items: WorkOrderLineView[] = response.data.workOrderLines || null;
            const totalRecords = response.data.pagination.totalRecords || 0;
            const filteredRecords = response.data.pagination.filteredRecords;
            const workOrder = this.store.selectSnapshot(WorkOrderState.currentPaginatedWorkOrders).find((wo) => wo.id === this.workOrderId);
            const deletedCasingCount = workOrder?.deletedCasingCount || 0;
            const isFiltered = !!rawSearchString && filteredRecords != null;
            const count = isFiltered
              ? filteredRecords
              : this.dealer.displayDeleteCasing === 'N' && this.isDeleteWorkOrderEnabled
              ? totalRecords - deletedCasingCount
              : totalRecords;

            this.store.dispatch(new UpdateCurrentPaginatedWorkOrderLinesAction(items));
            // Store the fetched items for the current page
            this.allWorkOrderLines = items;

            // State is now managed by the service, no need to dispatch here
            this.fetchComplete.emit();
            this.addPaginatorTooltip();
            setTimeout(() => this.addProcessCodeTooltip());
            return { items, count };
          }
        })
      );
  };

  onDeleteClick(workOrderLine: WorkOrderLineView) {
    this.dialog
      .open(SharedDeleteModalComponent, {
        data: {
          title: this.translationService.translate('deleteWorkOrderLineText'),
          messageTitle: this.translationService.translate('warningText'),
          message: this.translationService.translate('deleteWorkOrderLineDialogText'),
          buttonText: this.translationService.translate('yesDeleteText')
        }
      })
      .afterClosed()
      .subscribe((result) => {
        if (result === true) {
          this.workOrderService
            .markDeleteAndSaveCasing(this.dealer.id, this.workOrderId, workOrderLine.id, this.currentUserName)
            .subscribe({
              next: () => {
                this.alertService.showAlert({
                  type: AlertType.success,
                  content: this.translationService.translate('deleteSuccessfulText')
                } as Alert);
                this.tableRef?.resetFilters();
              },
              error: () =>
                this.alertService.showAlert({
                  type: AlertType.strong,
                  content: this.translationService.translate('deleteOperationFailedText')
                } as Alert)
            });
        }
      });
  }

  getSearchInputTranslation(): string {
    return `${this.translationService.translate('searchByText')} ${this.translationService.translate(
      'casingProcessCodeText'
    )}, ${this.translationService.translate('statusColumnText')}, ${this.translationService.translate(
      'makeText'
    )}, ${this.translationService.translate('seriesText')}, ${this.translationService.translate(
      'sizeText'
    )}, ${this.translationService.translate('treadText')}`;
  }
}
